import React, { useMemo, useState } from 'react';
import { ArrowDownToLine, Eye, TrendingUp, TrendingDown, Zap, DollarSign, Repeat, X, Globe, ChevronDown, ShoppingBag, Users, Lightbulb, ArrowRight, MapPin } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface StatsTabProps {
  showToast: (msg: string) => void;
  setActiveTab: (tab: string) => void;
}
type Period = 'week' | 'month' | 'quarter' | 'year';
interface PeriodData {
  views: number;
  viewsTrend: number;
  conversion: number;
  convTrend: number;
  avgCheck: number;
  avgCheckTrend: number;
  repeatClients: number;
  repeatTrend: number;
  income: number;
  incomeTrend: number;
  orders: number;
  ordersTrend: number;
}
const PERIOD_DATA: Record<Period, PeriodData> = {
  week: {
    views: 312,
    viewsTrend: 8,
    conversion: 9.1,
    convTrend: 0.5,
    avgCheck: 4900,
    avgCheckTrend: 2,
    repeatClients: 38,
    repeatTrend: 3,
    income: 78400,
    incomeTrend: 12,
    orders: 16,
    ordersTrend: 10
  },
  month: {
    views: 1247,
    viewsTrend: 15,
    conversion: 8.3,
    convTrend: 1.2,
    avgCheck: 4800,
    avgCheckTrend: 0,
    repeatClients: 34,
    repeatTrend: 5,
    income: 307200,
    incomeTrend: 22,
    orders: 64,
    ordersTrend: 18
  },
  quarter: {
    views: 3890,
    viewsTrend: 20,
    conversion: 8.0,
    convTrend: 0.8,
    avgCheck: 4750,
    avgCheckTrend: -1,
    repeatClients: 31,
    repeatTrend: 4,
    income: 890000,
    incomeTrend: 25,
    orders: 187,
    ordersTrend: 22
  },
  year: {
    views: 14200,
    viewsTrend: 35,
    conversion: 7.8,
    convTrend: 1.5,
    avgCheck: 4700,
    avgCheckTrend: 3,
    repeatClients: 29,
    repeatTrend: 8,
    income: 3200000,
    incomeTrend: 40,
    orders: 680,
    ordersTrend: 35
  }
};
const CHART_DATA = [{
  month: 'Май',
  orders: 24,
  revenue: 115000
}, {
  month: 'Июн',
  orders: 32,
  revenue: 154000
}, {
  month: 'Июл',
  orders: 28,
  revenue: 135000
}, {
  month: 'Авг',
  orders: 45,
  revenue: 216000
}, {
  month: 'Сен',
  orders: 52,
  revenue: 250000
}, {
  month: 'Окт',
  orders: 64,
  revenue: 307000
}];
const SOURCES = [{
  name: 'Каталог астрологов',
  percent: 52,
  orders: 33,
  revenue: 159000,
  color: 'bg-indigo-500'
}, {
  name: 'Поиск на платформе',
  percent: 28,
  orders: 18,
  revenue: 86000,
  color: 'bg-sky-400'
}, {
  name: 'Прямая ссылка',
  percent: 12,
  orders: 8,
  revenue: 38000,
  color: 'bg-emerald-400'
}, {
  name: 'Рекомендации',
  percent: 8,
  orders: 5,
  revenue: 24000,
  color: 'bg-amber-400'
}];
const CITIES = [{
  city: 'Москва',
  percent: 35,
  orders: 22,
  revenue: 106000
}, {
  city: 'Санкт-Петербург',
  percent: 18,
  orders: 12,
  revenue: 55000
}, {
  city: 'Казань',
  percent: 8,
  orders: 5,
  revenue: 24000
}, {
  city: 'Новосибирск',
  percent: 7,
  orders: 4,
  revenue: 21000
}, {
  city: 'Екатеринбург',
  percent: 5,
  orders: 3,
  revenue: 15000
}];
const TOP_SERVICES = [{
  name: 'Натальная карта (Видео)',
  orders: 42,
  conv: 12.4,
  revenue: 210000
}, {
  name: 'Соляр на год (Чат)',
  orders: 28,
  conv: 9.8,
  revenue: 112000
}, {
  name: 'Синастрия',
  orders: 15,
  conv: 5.2,
  revenue: 90000
}, {
  name: 'Экспресс-консультация',
  orders: 12,
  conv: 7.1,
  revenue: 36000
}];
const RECOMMENDATIONS = [{
  text: '«Натальная карта» — ваша самая популярная услуга. Попробуйте поднять цену на 10-15%.',
  action: 'Перейти к услугам',
  tab: 'services'
}, {
  text: 'Конверсия «Синастрии» ниже среднего (5.2%). Улучшите описание и добавьте примеры.',
  action: 'Редактировать',
  tab: 'services'
}, {
  text: '34% клиентов возвращаются. Предложите им скидку на повторный заказ.',
  action: 'Создать акцию',
  tab: 'promotion'
}];
export function StatsTab({
  showToast,
  setActiveTab
}: StatsTabProps) {
  const [period, setPeriod] = useState<Period>('month');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [selectedBarIdx, setSelectedBarIdx] = useState<number | null>(null);
  const [expandedSource, setExpandedSource] = useState<number | null>(null);
  const [expandedCity, setExpandedCity] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'orders' | 'conv' | 'revenue'>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const data = PERIOD_DATA[period];
  const maxOrders = Math.max(...CHART_DATA.map(d => d.orders));
  const formatMoney = (n: number) => new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(n);
  const sortedServices = useMemo(() => {
    return [...TOP_SERVICES].sort((a, b) => {
      const aVal = sortBy === 'orders' ? a.orders : sortBy === 'conv' ? a.conv : a.revenue;
      const bVal = sortBy === 'orders' ? b.orders : sortBy === 'conv' ? b.conv : b.revenue;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [sortBy, sortDir]);
  const handleSort = (col: 'orders' | 'conv' | 'revenue') => {
    if (sortBy === col) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };
  const TrendBadge = ({
    value
  }: {
    value: number;
  }) => {
    if (value === 0) return <span className="text-xs text-slate-500 font-medium" data-id="element-16022">
          Без изменений
        </span>;
    const isUp = value > 0;
    return <span className={`text-xs font-medium flex items-center gap-0.5 ${isUp ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-16023">
        {isUp ? <TrendingUp className="w-3 h-3" data-id="element-16024" /> : <TrendingDown className="w-3 h-3" data-id="element-16025" />}
        {isUp ? '+' : ''}
        {value}%
      </span>;
  };
  return <div className="space-y-6" data-id="element-16026">
      {/* Header + Period */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-16027">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-16028">Аналитика</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-16029">
          <div className="flex bg-slate-100 p-1 rounded-lg overflow-x-auto" data-id="element-16030">
            {(['week', 'month', 'quarter', 'year'] as Period[]).map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-1 sm:flex-none text-center ${period === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-16031">
                {{
              week: 'Неделя',
              month: 'Месяц',
              quarter: 'Квартал',
              year: 'Год'
            }[p]}
              </button>)}
          </div>
          <Button variant="outline" icon={<ArrowDownToLine className="w-4 h-4" data-id="element-16033" />} onClick={() => showToast('Отчёт скачивается...')} className="w-full sm:w-auto" data-id="element-16032">
            Скачать
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-16034">
        {[{
        key: 'views',
        label: 'Просмотры',
        value: data.views.toLocaleString('ru-RU'),
        trend: data.viewsTrend,
        icon: Eye,
        color: 'text-slate-400'
      }, {
        key: 'conversion',
        label: 'Конверсия',
        value: `${data.conversion}%`,
        trend: data.convTrend,
        icon: Zap,
        color: 'text-indigo-400'
      }, {
        key: 'avgCheck',
        label: 'Средний чек',
        value: formatMoney(data.avgCheck),
        trend: data.avgCheckTrend,
        icon: DollarSign,
        color: 'text-emerald-400'
      }, {
        key: 'repeat',
        label: 'Повторные',
        value: `${data.repeatClients}%`,
        trend: data.repeatTrend,
        icon: Repeat,
        color: 'text-amber-400'
      }, {
        key: 'income',
        label: 'Доход',
        value: formatMoney(data.income),
        trend: data.incomeTrend,
        icon: DollarSign,
        color: 'text-emerald-500',
        highlight: true
      }, {
        key: 'orders',
        label: 'Заказы',
        value: data.orders.toString(),
        trend: data.ordersTrend,
        icon: ShoppingBag,
        color: 'text-indigo-500',
        highlight: true
      }].map(card => <Card key={card.key} padding="sm" className={`cursor-pointer transition-all hover:border-indigo-300 ${expandedCard === card.key ? 'border-indigo-500 ring-1 ring-indigo-500' : ''} ${card.highlight ? 'bg-slate-50' : ''}`} onClick={() => setExpandedCard(expandedCard === card.key ? null : card.key)} data-id="element-16035">
            <div className="flex justify-between items-start mb-1" data-id="element-16036">
              <div className="text-xs text-slate-500 font-medium" data-id="element-16037">
                {card.label}
              </div>
              <card.icon className={`w-4 h-4 ${card.color}`} data-id="element-16038" />
            </div>
            <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-16039">
              {card.value}
            </div>
            <TrendBadge value={card.trend} data-id="element-16040" />
          </Card>)}
      </div>

      {/* Expanded Detail */}
      {expandedCard && <div className="animate-in fade-in slide-in-from-top-2" data-id="element-16041">
          <Card padding="md" className="bg-indigo-50/30 border-indigo-100 relative" data-id="element-16042">
            <button onClick={() => setExpandedCard(null)} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-white transition-colors" data-id="element-16043">
              <X className="w-4 h-4" data-id="element-16044" />
            </button>
            <h4 className="font-bold text-slate-900 mb-3 text-sm" data-id="element-16045">
              Детализация:{' '}
              {{
            views: 'Просмотры',
            conversion: 'Конверсия',
            avgCheck: 'Средний чек',
            repeat: 'Повторные клиенты',
            income: 'Доход',
            orders: 'Заказы'
          }[expandedCard]}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-16046">
              {expandedCard === 'views' && <>
                  <div data-id="element-16047">
                    <div className="text-xs text-slate-500" data-id="element-16048">Сегодня</div>
                    <div className="font-bold text-slate-900" data-id="element-16049">42</div>
                  </div>
                  <div data-id="element-16050">
                    <div className="text-xs text-slate-500" data-id="element-16051">Вчера</div>
                    <div className="font-bold text-slate-900" data-id="element-16052">38</div>
                  </div>
                  <div data-id="element-16053">
                    <div className="text-xs text-slate-500" data-id="element-16054">Из каталога</div>
                    <div className="font-bold text-slate-900" data-id="element-16055">65%</div>
                  </div>
                  <div data-id="element-16056">
                    <div className="text-xs text-slate-500" data-id="element-16057">Из поиска</div>
                    <div className="font-bold text-slate-900" data-id="element-16058">28%</div>
                  </div>
                </>}
              {expandedCard === 'conversion' && <>
                  <div data-id="element-16059">
                    <div className="text-xs text-slate-500" data-id="element-16060">Просмотры</div>
                    <div className="font-bold text-slate-900" data-id="element-16061">
                      {data.views.toLocaleString('ru-RU')}
                    </div>
                  </div>
                  <div data-id="element-16062">
                    <div className="text-xs text-slate-500" data-id="element-16063">Контакты</div>
                    <div className="font-bold text-slate-900" data-id="element-16064">
                      {Math.round(data.views * 0.24)}
                    </div>
                  </div>
                  <div data-id="element-16065">
                    <div className="text-xs text-slate-500" data-id="element-16066">Заказы</div>
                    <div className="font-bold text-indigo-600" data-id="element-16067">
                      {data.orders}
                    </div>
                  </div>
                  <div data-id="element-16068">
                    <div className="text-xs text-slate-500" data-id="element-16069">Итоговая</div>
                    <div className="font-bold text-emerald-600" data-id="element-16070">
                      {data.conversion}%
                    </div>
                  </div>
                </>}
              {expandedCard === 'avgCheck' && <>
                  <div data-id="element-16071">
                    <div className="text-xs text-slate-500" data-id="element-16072">До 3 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-16073">20%</div>
                  </div>
                  <div data-id="element-16074">
                    <div className="text-xs text-slate-500" data-id="element-16075">3-5 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-16076">55%</div>
                  </div>
                  <div data-id="element-16077">
                    <div className="text-xs text-slate-500" data-id="element-16078">Более 5 000 ₽</div>
                    <div className="font-bold text-slate-900" data-id="element-16079">25%</div>
                  </div>
                  <div data-id="element-16080">
                    <div className="text-xs text-slate-500" data-id="element-16081">Макс. чек</div>
                    <div className="font-bold text-emerald-600" data-id="element-16082">15 000 ₽</div>
                  </div>
                </>}
              {expandedCard === 'repeat' && <>
                  <div data-id="element-16083">
                    <div className="text-xs text-slate-500" data-id="element-16084">Всего клиентов</div>
                    <div className="font-bold text-slate-900" data-id="element-16085">104</div>
                  </div>
                  <div data-id="element-16086">
                    <div className="text-xs text-slate-500" data-id="element-16087">Новые</div>
                    <div className="font-bold text-slate-900" data-id="element-16088">69 (66%)</div>
                  </div>
                  <div data-id="element-16089">
                    <div className="text-xs text-slate-500" data-id="element-16090">Повторные</div>
                    <div className="font-bold text-indigo-600" data-id="element-16091">35 (34%)</div>
                  </div>
                  <div data-id="element-16092">
                    <div className="text-xs text-slate-500" data-id="element-16093">3+ заказа</div>
                    <div className="font-bold text-emerald-600" data-id="element-16094">12 чел.</div>
                  </div>
                </>}
              {expandedCard === 'income' && <>
                  <div data-id="element-16095">
                    <div className="text-xs text-slate-500" data-id="element-16096">Услуги</div>
                    <div className="font-bold text-slate-900" data-id="element-16097">
                      {formatMoney(data.income * 0.85)}
                    </div>
                  </div>
                  <div data-id="element-16098">
                    <div className="text-xs text-slate-500" data-id="element-16099">Товары</div>
                    <div className="font-bold text-slate-900" data-id="element-16100">
                      {formatMoney(data.income * 0.15)}
                    </div>
                  </div>
                  <div data-id="element-16101">
                    <div className="text-xs text-slate-500" data-id="element-16102">Комиссия (20%)</div>
                    <div className="font-bold text-rose-600" data-id="element-16103">
                      -{formatMoney(data.income * 0.2)}
                    </div>
                  </div>
                  <div data-id="element-16104">
                    <div className="text-xs text-slate-500" data-id="element-16105">На руки</div>
                    <div className="font-bold text-emerald-600" data-id="element-16106">
                      {formatMoney(data.income * 0.8)}
                    </div>
                  </div>
                </>}
              {expandedCard === 'orders' && <>
                  <div data-id="element-16107">
                    <div className="text-xs text-slate-500" data-id="element-16108">Завершено</div>
                    <div className="font-bold text-emerald-600" data-id="element-16109">
                      {Math.round(data.orders * 0.85)}
                    </div>
                  </div>
                  <div data-id="element-16110">
                    <div className="text-xs text-slate-500" data-id="element-16111">В работе</div>
                    <div className="font-bold text-amber-600" data-id="element-16112">
                      {Math.round(data.orders * 0.1)}
                    </div>
                  </div>
                  <div data-id="element-16113">
                    <div className="text-xs text-slate-500" data-id="element-16114">Отменено</div>
                    <div className="font-bold text-rose-600" data-id="element-16115">
                      {Math.round(data.orders * 0.05)}
                    </div>
                  </div>
                  <div data-id="element-16116">
                    <div className="text-xs text-slate-500" data-id="element-16117">Средний/день</div>
                    <div className="font-bold text-slate-900" data-id="element-16118">
                      {(data.orders / (period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365)).toFixed(1)}
                    </div>
                  </div>
                </>}
            </div>
          </Card>
        </div>}

      {/* Orders Chart */}
      <Card padding="md" data-id="element-16119">
        <h3 className="font-bold text-slate-900 mb-5" data-id="element-16120">Заказы по месяцам</h3>
        <div className="h-48 flex items-end justify-between gap-2 sm:gap-4 border-b border-slate-100 pb-2" data-id="element-16121">
          {CHART_DATA.map((bar, i) => {
          const heightPct = bar.orders / maxOrders * 100;
          const isSelected = selectedBarIdx === i;
          return <div key={i} className="flex flex-col items-center flex-1 group cursor-pointer" onClick={() => setSelectedBarIdx(isSelected ? null : i)} data-id="element-16122">
                <div className={`text-xs mb-1.5 font-bold transition-opacity ${isSelected ? 'opacity-100 text-indigo-600' : 'opacity-0 group-hover:opacity-100 text-slate-400'}`} data-id="element-16123">
                  {bar.orders}
                </div>
                <div className={`w-full max-w-[44px] rounded-t-md transition-colors ${isSelected ? 'bg-indigo-600' : 'bg-indigo-200 group-hover:bg-indigo-400'}`} style={{
              height: `${heightPct}%`
            }} data-id="element-16124" />
                <div className={`text-xs mt-2 font-medium ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-16125">
                  {bar.month}
                </div>
              </div>;
        })}
        </div>

        {selectedBarIdx !== null && <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 animate-in fade-in slide-in-from-top-2" data-id="element-16126">
            <div className="flex flex-wrap gap-4 text-sm" data-id="element-16127">
              <span className="text-slate-600" data-id="element-16128">
                Заказов:{' '}
                <strong className="text-slate-900" data-id="element-16129">
                  {CHART_DATA[selectedBarIdx].orders}
                </strong>
              </span>
              <span className="text-slate-600" data-id="element-16130">
                Выручка:{' '}
                <strong className="text-emerald-600" data-id="element-16131">
                  {formatMoney(CHART_DATA[selectedBarIdx].revenue)}
                </strong>
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => {
          showToast(`Заказы за ${CHART_DATA[selectedBarIdx!].month}`);
          setActiveTab('orders');
        }} data-id="element-16132">
              К заказам <ArrowRight className="w-3.5 h-3.5 ml-1" data-id="element-16133" />
            </Button>
          </div>}
      </Card>

      {/* Sources + Geography */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-16134">
        {/* Sources */}
        <Card padding="md" data-id="element-16135">
          <h3 className="font-bold text-slate-900 mb-5" data-id="element-16136">Источники клиентов</h3>
          <div className="space-y-4" data-id="element-16137">
            {SOURCES.map((src, i) => <div key={i} data-id="element-16138">
                <div className="cursor-pointer group" onClick={() => setExpandedSource(expandedSource === i ? null : i)} data-id="element-16139">
                  <div className="flex justify-between text-sm mb-1.5" data-id="element-16140">
                    <span className="font-medium text-slate-700 group-hover:text-indigo-600 transition-colors" data-id="element-16141">
                      {src.name}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-16142">
                      {src.percent}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-16143">
                    <div className={`h-full ${src.color} rounded-full`} style={{
                  width: `${src.percent}%`
                }} data-id="element-16144" />
                  </div>
                </div>
                {expandedSource === i && <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 flex gap-6 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-16145">
                    <div data-id="element-16146">
                      <span className="text-slate-500" data-id="element-16147">Заказов:</span>{' '}
                      <strong className="text-slate-900" data-id="element-16148">{src.orders}</strong>
                    </div>
                    <div data-id="element-16149">
                      <span className="text-slate-500" data-id="element-16150">Доход:</span>{' '}
                      <strong className="text-emerald-600" data-id="element-16151">
                        {formatMoney(src.revenue)}
                      </strong>
                    </div>
                  </div>}
              </div>)}
          </div>
        </Card>

        {/* Geography */}
        <Card padding="md" data-id="element-16152">
          <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-16153">
            <Globe className="w-5 h-5 text-slate-400" data-id="element-16154" /> География
          </h3>
          <div className="space-y-3" data-id="element-16155">
            {CITIES.map((geo, i) => <div key={i} data-id="element-16156">
                <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setExpandedCity(expandedCity === i ? null : i)} data-id="element-16157">
                  <div className="w-5 text-center text-xs font-bold text-slate-400" data-id="element-16158">
                    {i + 1}
                  </div>
                  <div className="flex-1" data-id="element-16159">
                    <div className="flex justify-between text-sm mb-1" data-id="element-16160">
                      <span className="font-medium text-slate-700 group-hover:text-indigo-600 transition-colors flex items-center gap-1" data-id="element-16161">
                        <MapPin className="w-3 h-3" data-id="element-16162" /> {geo.city}
                      </span>
                      <span className="font-bold text-slate-900" data-id="element-16163">
                        {geo.percent}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-16164">
                      <div className="bg-slate-400 h-full rounded-full group-hover:bg-indigo-500 transition-colors" style={{
                    width: `${geo.percent}%`
                  }} data-id="element-16165" />
                    </div>
                  </div>
                </div>
                {expandedCity === i && <div className="ml-8 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 flex gap-6 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-16166">
                    <div data-id="element-16167">
                      <span className="text-slate-500" data-id="element-16168">Заказов:</span>{' '}
                      <strong className="text-slate-900" data-id="element-16169">{geo.orders}</strong>
                    </div>
                    <div data-id="element-16170">
                      <span className="text-slate-500" data-id="element-16171">Доход:</span>{' '}
                      <strong className="text-emerald-600" data-id="element-16172">
                        {formatMoney(geo.revenue)}
                      </strong>
                    </div>
                  </div>}
              </div>)}
            <div className="pt-2 mt-1 border-t border-slate-100 text-sm text-slate-500 text-center cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => showToast('Все города')} data-id="element-16173">
              Другие города: 27%
            </div>
          </div>
        </Card>
      </div>

      {/* Top Services */}
      <Card padding="none" className="overflow-hidden" data-id="element-16174">
        <div className="p-4 sm:p-5 border-b border-slate-100" data-id="element-16175">
          <h3 className="font-bold text-slate-900" data-id="element-16176">Топ услуги</h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-16177">
          <table className="w-full text-left text-sm" data-id="element-16178">
            <thead className="bg-slate-50/50 text-slate-500 border-b border-slate-100" data-id="element-16179">
              <tr data-id="element-16180">
                <th className="px-5 py-3 font-semibold" data-id="element-16181">Услуга</th>
                <th className="px-5 py-3 font-semibold cursor-pointer hover:text-slate-900" onClick={() => handleSort('orders')} data-id="element-16182">
                  <span className="flex items-center gap-1" data-id="element-16183">
                    Заказы{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'orders' ? 'text-indigo-600' : ''} ${sortBy === 'orders' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-16184" />
                  </span>
                </th>
                <th className="px-5 py-3 font-semibold cursor-pointer hover:text-slate-900" onClick={() => handleSort('conv')} data-id="element-16185">
                  <span className="flex items-center gap-1" data-id="element-16186">
                    Конверсия{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'conv' ? 'text-indigo-600' : ''} ${sortBy === 'conv' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-16187" />
                  </span>
                </th>
                <th className="px-5 py-3 font-semibold text-right cursor-pointer hover:text-slate-900" onClick={() => handleSort('revenue')} data-id="element-16188">
                  <span className="flex items-center justify-end gap-1" data-id="element-16189">
                    Доход{' '}
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortBy === 'revenue' ? 'text-indigo-600' : ''} ${sortBy === 'revenue' && sortDir === 'asc' ? 'rotate-180' : ''}`} data-id="element-16190" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-16191">
              {sortedServices.map((svc, i) => <tr key={i} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => {
              showToast(`Заказы: ${svc.name}`);
              setActiveTab('orders');
            }} data-id="element-16192">
                  <td className="px-5 py-3 font-medium text-slate-900" data-id="element-16193">
                    {svc.name}
                  </td>
                  <td className="px-5 py-3 text-slate-600" data-id="element-16194">{svc.orders}</td>
                  <td className="px-5 py-3 text-emerald-600" data-id="element-16195">{svc.conv}%</td>
                  <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-16196">
                    {formatMoney(svc.revenue)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden divide-y divide-slate-100" data-id="element-16197">
          {sortedServices.map((svc, i) => <div key={i} className="p-4 cursor-pointer" onClick={() => {
          showToast(`Заказы: ${svc.name}`);
          setActiveTab('orders');
        }} data-id="element-16198">
              <div className="font-bold text-slate-900 text-sm mb-2" data-id="element-16199">
                {svc.name}
              </div>
              <div className="flex justify-between text-sm" data-id="element-16200">
                <span className="text-slate-500" data-id="element-16201">
                  Заказов:{' '}
                  <strong className="text-slate-900" data-id="element-16202">{svc.orders}</strong>
                </span>
                <span className="text-slate-500" data-id="element-16203">
                  Конв:{' '}
                  <strong className="text-emerald-600" data-id="element-16204">{svc.conv}%</strong>
                </span>
                <span className="font-bold text-slate-900" data-id="element-16205">
                  {formatMoney(svc.revenue)}
                </span>
              </div>
            </div>)}
        </div>
      </Card>

      {/* Recommendations */}
      <Card padding="md" data-id="element-16206">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-16207">
          <Lightbulb className="w-5 h-5 text-amber-500" data-id="element-16208" /> Рекомендации
        </h3>
        <div className="space-y-3" data-id="element-16209">
          {RECOMMENDATIONS.map((rec, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-amber-50/50 border border-amber-100 rounded-xl" data-id="element-16210">
              <p className="text-sm text-slate-700 flex-1" data-id="element-16211">{rec.text}</p>
              <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 shrink-0" onClick={() => {
            showToast(rec.action);
            setActiveTab(rec.tab);
          }} data-id="element-16212">
                {rec.action} <ArrowRight className="w-3.5 h-3.5 ml-1" data-id="element-16213" />
              </Button>
            </div>)}
        </div>
      </Card>
    </div>;
}